/**
 * Holds the in-flight export jobs for the whole TPO Gen area and renders one
 * poller per job, so a download keeps working while the user moves between
 * pages. Mounted above `CustomerConfigProvider` on purpose: that provider
 * blocks rendering while the customer config loads, and a queued export must
 * not stall behind it.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';

import ExportJobPoller from '../components/ExportJobPoller';
import { readExportJobs, writeExportJobs } from '../utils/exportJobStorage';
import type { ExportJobRecord, ReportKind } from '../types';

interface ExportJobsContextValue {
    addJob: (job: ExportJobRecord) => void;
    /** Guards duplicate submissions from this tab; see contract section 9. */
    isExportPending: (planId: string, reportKind: ReportKind) => boolean;
}

const ExportJobsContext = createContext<ExportJobsContextValue | null>(null);

const pendingKey = (planId: string, reportKind: ReportKind): string => `${planId}:${reportKind}`;

export const ExportJobsProvider: FC<{ children: ReactNode }> = ({ children }) => {
    // Hydrated lazily so a remount — including a route change within TPO Gen —
    // picks the jobs back up instead of orphaning them.
    const [jobs, setJobs] = useState<ExportJobRecord[]>(readExportJobs);

    const addJob = useCallback((job: ExportJobRecord) => {
        setJobs((current) => {
            if (current.some((existing) => existing.exportId === job.exportId)) return current;
            const next = [...current, job];
            writeExportJobs(next);
            return next;
        });
    }, []);

    const removeJob = useCallback((exportId: string) => {
        setJobs((current) => {
            const next = current.filter((job) => job.exportId !== exportId);
            if (next.length === current.length) return current;
            writeExportJobs(next);
            return next;
        });
    }, []);

    const pendingKeys = useMemo(() => new Set(jobs.map((job) => pendingKey(job.planId, job.reportKind))), [jobs]);

    const isExportPending = useCallback(
        (planId: string, reportKind: ReportKind) => pendingKeys.has(pendingKey(planId, reportKind)),
        [pendingKeys],
    );

    const value = useMemo<ExportJobsContextValue>(
        () => ({ addJob, isExportPending }),
        [addJob, isExportPending],
    );

    return (
        <ExportJobsContext.Provider value={value}>
            {children}
            {jobs.map((job) => (
                <ExportJobPoller key={job.exportId} job={job} onSettled={removeJob} />
            ))}
        </ExportJobsContext.Provider>
    );
};

ExportJobsProvider.displayName = 'ExportJobsProvider';

export function useExportJobs(): ExportJobsContextValue {
    const ctx = useContext(ExportJobsContext);
    if (!ctx) {
        throw new Error('useExportJobs must be used within an ExportJobsProvider (TPO Gen routes only)');
    }
    return ctx;
}
